import { http } from "./client";
import type { PageParams } from "@/types/api";
import type {
  Announcement,
  AnnouncementAudience,
  AnnouncementAudienceRole,
  AnnouncementPriority,
  AnnouncementStatus,
  AnnouncementType,
} from "@/types/domain";

export interface AnnouncementFilters extends PageParams {
  unread_only?: boolean;
  type?: AnnouncementType | "";
  status?: AnnouncementStatus | "";
  priority?: AnnouncementPriority | "";
  /** active = inside its window now; upcoming = not started; expired = ended. */
  window?: "active" | "upcoming" | "expired" | "";
}

/** Create/update body. Every popup field is optional on the wire - an
 * omitted field keeps its stored value on edit. */
export interface AnnouncementInput {
  title: string;
  body: string;
  type?: AnnouncementType;
  status?: AnnouncementStatus;
  priority?: AnnouncementPriority;
  image_url?: string;
  starts_at?: string | null;
  ends_at?: string | null;
  event_at?: string | null;
  location?: string;
  target_audience?: AnnouncementAudience;
  target_roles?: string[];
  cta_text?: string;
  cta_url?: string;
  show_on_login?: boolean;
}

/** Announcements module. Read endpoints are announcement.view (the backend
 * narrows a non-manager to published, started rows in their audience);
 * authoring is announcement.manage, surfaced on ManageAnnouncementsPage. */
export const announcementsApi = {
  list: (filters: AnnouncementFilters = {}) => http.getPage<Announcement>("/announcements", filters),
  get: (id: string) => http.get<Announcement>(`/announcements/${id}`),
  unreadCount: () => http.get<{ count: number }>("/announcements/unread-count"),
  markRead: (id: string) => http.post<void>(`/announcements/${id}/read`, {}),
  markAllRead: () => http.post<void>("/announcements/read-all", {}),

  // Login popup
  loginPopup: () => http.get<Announcement[]>("/announcements/login-popup"),
  markPopupViewed: (id: string) => http.post<void>(`/announcements/${id}/view`, {}),
  dismissPopup: (id: string) => http.post<void>(`/announcements/${id}/dismiss`, {}),

  // Authoring (announcement.manage)
  create: (input: AnnouncementInput) => http.post<Announcement>("/announcements", input),
  update: (id: string, input: AnnouncementInput) => http.put<Announcement>(`/announcements/${id}`, input),
  remove: (id: string) => http.delete<void>(`/announcements/${id}`),
  publish: (id: string) => http.post<Announcement>(`/announcements/${id}/publish`, {}),
  unpublish: (id: string) => http.post<Announcement>(`/announcements/${id}/unpublish`, {}),
  audienceRoles: () => http.get<AnnouncementAudienceRole[]>("/announcements/audience-roles"),
  uploadBanner: (file: File) => {
    const form = new FormData();
    form.append("image", file);
    return http.post<{ url: string }>("/announcements/banner", form);
  },
};
